import { createSlice } from "@reduxjs/toolkit";
import { createSelector } from "reselect";

// Initial state with an empty object for assets (normalized)
const initialState = { assets: {} };

// Helper function to sort assets by name
const sortAssetsByName = assetsObject => {
	const sortedEntries = Object.entries(assetsObject).sort(([, a], [, b]) =>
		(a.name || "").localeCompare(b.name || "", undefined, {
			numeric: true
		})
	);
	return Object.fromEntries(sortedEntries);
};

const assetSlice = createSlice({
	name: "asset",
	initialState,
	reducers: {
		// Update an asset only if it has changed or doesn't exist
		updateAsset: (state, action) => {
			const currentAsset = state.assets[action.payload.id];
			if (!currentAsset || JSON.stringify(currentAsset) !== JSON.stringify(action.payload)) {
				state.assets[action.payload.id] = action.payload;
				state.assets = sortAssetsByName(state.assets);
			}
		},
		// Delete an asset by ID
		deleteAsset: (state, action) => {
			delete state.assets[action.payload];
		},
		// Add a new asset to the object and sort by name
		createAsset: (state, action) => {
			state.assets[action.payload.id] = action.payload;
			// Sort assets by name after creation
			state.assets = sortAssetsByName(state.assets);
		},
		createAssets: (state, action) => {
			action.payload.forEach(asset => {
				state.assets[asset.id] = asset;
			});
			// Sort assets by name after creation
			state.assets = sortAssetsByName(state.assets);
		},
		// Replace all assets with a normalized object from an array, sorted by name
		setAssets: (state, action) => {
			state.assets = action.payload.reduce((acc, asset) => {
				acc[asset.id] = asset;
				return acc;
			}, {});
		}
	}
});

// Export the actions
export const { createAsset, createAssets, deleteAsset, updateAsset, setAssets } =
	assetSlice.actions;

// Base selector to get the assets as an object
export const selectAssetsObject = state => state.asset.assets;

// Selector to get assets as an array
export const selectAssets = createSelector([selectAssetsObject], assets => Object.values(assets));

// Memoized selector to filter assets by folderId
export const selectAssetsByFolderId = createSelector(
	[selectAssets, (_, folderId) => folderId],
	(assets, folderId) =>
		assets
			.filter(asset => asset.folderId === folderId)
			.sort((a, b) =>
				(a.name || "").localeCompare(b.name || "", undefined, {
					numeric: true,
					sensitivity: "base"
				})
			)
);

// Memoized selector to get an asset by ID
export const selectAssetById = createSelector(
	[selectAssetsObject, (_, assetId) => assetId],
	(assets, assetId) => assets[assetId] || null
);

// New memoized selector to filter assets by fileType
export const selectAssetsByAssetType = createSelector(
	[selectAssets, (_, fileType) => fileType],
	(assets, fileType) => assets.filter(asset => asset.fileType === fileType)
);

// Memoized selector to filter assets by an array of fileTypes
export const selectAssetsByAssetTypes = createSelector(
	[selectAssets, (_, fileTypes) => fileTypes],
	(assets, fileTypes) =>
		Array.isArray(fileTypes) ? assets.filter(asset => fileTypes.includes(asset.fileType)) : []
);

// Export the reducer
export default assetSlice.reducer;
